/* eslint-disable */
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import {
  ClipboardText, Receipt, ShieldCheck, ChartLine, Users, ArrowUpRight, Sparkle,
  PencilSimple, FloppyDisk, X, Plus, Trash, CheckCircle, Image as ImageIcon,
  Flask, Gavel, Package,
} from '@phosphor-icons/react';
import TI from '../../components/brand/TI';
import { HaleWordmark } from '../../components/HaleBrand';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const IVORY = '#F5F1E8';
const SLATE = '#0B1426';
const CORAL = '#FF4E6A';
const MUTED = '#5B6577';
const RULE  = 'rgba(11,20,38,0.10)';

const HALE_TOOLS = [
  { href: '/assessor/hale-applications',
    icon: ClipboardText,
    title: 'Hale Applications',
    desc: 'Brand intake, eligibility review, approval / revision decisions.' },
  { href: '/admin/hale-reviewed',
    icon: Package,
    title: 'Brand Submissions',
    desc: 'Products submitted by brands for Hale Reviewed — the live intake queue.' },
  { href: '/admin/hale-reviewed',
    icon: Flask,
    title: 'Product Reviews',
    desc: 'Open a submitted product: INCI, claims, documents, status history, review controls.' },
  { href: '/admin/hale-reviewed',
    icon: ClipboardText,
    title: 'Hale Reviewed Queue',
    desc: 'Filterable queue of all Hale Reviewed products across every status.' },
  { href: '/admin/hale-reviewed',
    icon: Receipt,
    title: 'Documentation Requests',
    desc: 'Request and track supporting documents from brands (COA, SDS, stability, claims substantiation).' },
  { href: '/admin/hale/decisions',
    icon: Gavel,
    title: 'Review Decisions',
    desc: 'Final Hale program decisions · approve, hold, reject, issue Reviewed™ / Verified™ badge.' },
  { href: '/admin/hale/samples',
    icon: ClipboardText,
    title: 'Reports',
    desc: 'Workflow output reports at every stage — intake, AG audit, Hale decision, badge, dashboard, emails, PDF.' },
  { href: '/assessor/hale-review',
    icon: Flask,
    title: 'AG reviewer queue',
    desc: 'Scientific authority · audit, internal notes, released findings, recommendation. AG / ToxIntel reviewers only.' },
  { href: '/assessor/hale-invoices',
    icon: Receipt,
    title: 'Invoices',
    desc: 'Membership activation, ACH / wire / bill.com invoice requests.' },
  { href: '/assessor/hale-verified',
    icon: ShieldCheck,
    title: 'Verified™ console',
    desc: 'Sustainability and supplier-supported documentation reviews.' },
  { href: '/assessor/hale-command',
    icon: ChartLine,
    title: 'Command center',
    desc: 'Reviewer throughput, queue health, and AI quality flags.' },
  { href: '/assessor/hale',
    icon: Users,
    title: 'Brand pool',
    desc: 'All Hale-onboarded brands · tiering · contact metadata · pilots.' },
  { href: '/admin/hale/marketing',
    icon: ImageIcon,
    title: 'Marketing assets',
    desc: 'Pilot launch visuals · demo deck imagery · co-branded LinkedIn graphics · executive one-pager.' },
];

function PilotEditDialog({ pilot, options, onClose, onSaved }) {
  const [form, setForm] = useState({
    name:                pilot.name || '',
    track:               pilot.track || 'tbd',
    tier:                pilot.tier  || 'tbd',
    pilot_status:        pilot.pilot_status || 'draft',
    assigned_reviewer:   pilot.assigned_reviewer || '',
    documents_submitted: Array.isArray(pilot.documents_submitted) ? pilot.documents_submitted : [],
    next_action:         pilot.next_action || '',
    notes:               pilot.notes || '',
  });
  const [newDoc, setNewDoc] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const payload = {
        ...form,
        assigned_reviewer: form.assigned_reviewer || null,
      };
      const r = await axios.put(`${API_URL}/api/hale/brands/${pilot.id}`, payload, { withCredentials: true });
      onSaved(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const addDoc = () => {
    if (!newDoc.trim()) return;
    setForm(f => ({ ...f, documents_submitted: [...f.documents_submitted, newDoc.trim()] }));
    setNewDoc('');
  };
  const removeDoc = (idx) => {
    setForm(f => ({ ...f, documents_submitted: f.documents_submitted.filter((_, i) => i !== idx) }));
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(11,20,38,0.55)' }}
      onClick={onClose}
      data-testid="pilot-edit-dialog"
    >
      <div
        className="rounded-sm border w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        style={{ borderColor: RULE, backgroundColor: IVORY, color: SLATE, fontFamily: 'Inter, system-ui, sans-serif' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-6 border-b" style={{ borderColor: RULE }}>
          <div>
            <p className="text-[10px] font-mono uppercase tracking-[0.3em]" style={{ color: CORAL }}>
              Edit pilot brand
            </p>
            <h3 className="text-2xl font-extrabold tracking-tight mt-1"
                style={{ fontFamily: '"Cormorant Garamond", serif', color: SLATE }}>
              {pilot.name}
            </h3>
          </div>
          <button onClick={onClose} aria-label="Close" data-testid="pilot-edit-close" style={{ color: MUTED }}>
            <X size={20} weight="bold" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          <Field label="Brand name">
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
              className="w-full px-3 py-2 rounded-sm border bg-white text-sm"
              style={{ borderColor: RULE, color: SLATE }}
              data-testid="pilot-edit-name"
            />
          </Field>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Pathway (Reviewed / Verified)">
              <select
                value={form.track}
                onChange={(e) => setForm(f => ({ ...f, track: e.target.value }))}
                className="w-full px-3 py-2 rounded-sm border bg-white text-sm"
                style={{ borderColor: RULE, color: SLATE }}
                data-testid="pilot-edit-track"
              >
                {(options?.tracks || []).map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
              </select>
            </Field>
            <Field label="Company size / tier">
              <select
                value={form.tier}
                onChange={(e) => setForm(f => ({ ...f, tier: e.target.value }))}
                className="w-full px-3 py-2 rounded-sm border bg-white text-sm"
                style={{ borderColor: RULE, color: SLATE }}
                data-testid="pilot-edit-tier"
              >
                {(options?.tiers || []).map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
              </select>
            </Field>
          </div>

          <Field label="Status">
            <select
              value={form.pilot_status}
              onChange={(e) => setForm(f => ({ ...f, pilot_status: e.target.value }))}
              className="w-full px-3 py-2 rounded-sm border bg-white text-sm"
              style={{ borderColor: RULE, color: SLATE }}
              data-testid="pilot-edit-status"
            >
              {(options?.pilot_statuses || []).map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
            </select>
          </Field>

          <Field label="Assigned reviewer">
            <input
              type="text"
              value={form.assigned_reviewer}
              onChange={(e) => setForm(f => ({ ...f, assigned_reviewer: e.target.value }))}
              placeholder="Reviewer name or email"
              className="w-full px-3 py-2 rounded-sm border bg-white text-sm"
              style={{ borderColor: RULE, color: SLATE }}
              data-testid="pilot-edit-reviewer"
            />
          </Field>

          <Field label="Documents submitted">
            <div className="space-y-2">
              {form.documents_submitted.length === 0 && (
                <p className="text-[12px] italic" style={{ color: MUTED }}>No documents submitted yet.</p>
              )}
              {form.documents_submitted.map((doc, idx) => (
                <div key={idx} className="flex items-center gap-2 text-sm" data-testid={`pilot-edit-doc-${idx}`}>
                  <CheckCircle size={14} weight="fill" style={{ color: CORAL }} />
                  <span className="flex-1" style={{ color: SLATE }}>{doc}</span>
                  <button onClick={() => removeDoc(idx)} aria-label="Remove" style={{ color: MUTED }}>
                    <Trash size={14} weight="bold" />
                  </button>
                </div>
              ))}
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newDoc}
                  onChange={(e) => setNewDoc(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addDoc(); } }}
                  placeholder="Document name (e.g. Ingredient list v2.csv)"
                  className="flex-1 px-3 py-2 rounded-sm border bg-white text-sm"
                  style={{ borderColor: RULE, color: SLATE }}
                  data-testid="pilot-edit-doc-input"
                />
                <button
                  onClick={addDoc}
                  className="px-3 py-2 rounded-sm text-[11px] font-mono uppercase tracking-wider border"
                  style={{ borderColor: RULE, color: SLATE }}
                  data-testid="pilot-edit-doc-add"
                >
                  <Plus size={12} weight="bold" />
                </button>
              </div>
            </div>
          </Field>

          <Field label="Next action">
            <textarea
              value={form.next_action}
              onChange={(e) => setForm(f => ({ ...f, next_action: e.target.value }))}
              rows={2}
              placeholder="e.g. Confirm brand size and pathway with Hale."
              className="w-full px-3 py-2 rounded-sm border bg-white text-sm"
              style={{ borderColor: RULE, color: SLATE }}
              data-testid="pilot-edit-next-action"
            />
          </Field>

          <Field label="Internal notes">
            <textarea
              value={form.notes}
              onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))}
              rows={2}
              className="w-full px-3 py-2 rounded-sm border bg-white text-sm"
              style={{ borderColor: RULE, color: SLATE }}
              data-testid="pilot-edit-notes"
            />
          </Field>

          {error && (
            <p className="text-[13px]" style={{ color: '#B0312D' }} data-testid="pilot-edit-error">{error}</p>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 p-6 border-t" style={{ borderColor: RULE }}>
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-sm text-[12px] font-medium uppercase tracking-wider"
            style={{ color: MUTED }}
            data-testid="pilot-edit-cancel"
          >
            Cancel
          </button>
          <button
            onClick={save}
            disabled={saving}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-sm text-[12px] font-medium uppercase tracking-wider shadow-sm disabled:opacity-50"
            style={{ backgroundColor: CORAL, color: SLATE }}
            data-testid="pilot-edit-save"
          >
            {saving ? 'Saving…' : 'Save changes'} <FloppyDisk size={13} weight="fill" />
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[10px] font-mono uppercase tracking-[0.25em] mb-1.5 block" style={{ color: MUTED }}>
        {label}
      </span>
      {children}
    </label>
  );
}

function labelFor(list, id) {
  if (!list || !id) return '—';
  return list.find(o => o.id === id)?.label || id;
}

export default function HaleAdminHubPage() {
  const [stats, setStats] = useState(null);
  const [pilots, setPilots] = useState(null);
  const [options, setOptions] = useState(null);
  const [seedResult, setSeedResult] = useState(null);
  const [seeding, setSeeding] = useState(false);
  const [editing, setEditing] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/hale-marketplace/public/stats`);
      setStats(r.data || null);
    } catch { /* swallow */ }
    try {
      const r = await axios.get(`${API_URL}/api/hale/pilots`, { withCredentials: true });
      setPilots(r.data || null);
    } catch { /* swallow */ }
    try {
      const r = await axios.get(`${API_URL}/api/hale/admin/options`, { withCredentials: true });
      setOptions(r.data || null);
    } catch { /* swallow */ }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const seedPilots = async () => {
    setSeeding(true);
    setSeedResult(null);
    try {
      const r = await axios.post(`${API_URL}/api/hale/seed-pilots`, {}, { withCredentials: true });
      setSeedResult({ ok: true, ...r.data });
      refresh();
    } catch (e) {
      setSeedResult({ ok: false, error: e?.response?.data?.detail || 'Seed failed.' });
    } finally {
      setSeeding(false);
    }
  };

  const onPilotSaved = (updated) => {
    setPilots(p => ({
      ...p,
      items: (p?.items || []).map(it => it.id === updated.id ? updated : it),
    }));
    setEditing(null);
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: IVORY, color: SLATE, fontFamily: 'Inter, system-ui, sans-serif' }}>
      <Helmet>
        <title>ToxIntel Assessor Center — Hale</title>
      </Helmet>

      <header className="border-b" style={{ borderColor: RULE }}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" className="flex items-center gap-3" aria-label="ToxIntel home">
              <span className="text-xl tracking-tight" style={{ color: SLATE, fontFamily: '"Cormorant Garamond", serif' }}>
                <TI color={CORAL} />
              </span>
            </Link>
            <span
              className="ml-3 text-[10px] font-mono uppercase tracking-[0.25em] px-2 py-1 rounded-sm"
              style={{ backgroundColor: SLATE, color: IVORY }}
            >
              · Assessor Center · Hale
            </span>
          </div>
          <nav className="flex items-center gap-5">
            <Link to="/admin" className="text-[11px] uppercase tracking-[0.18em]" style={{ color: MUTED }} data-testid="hale-admin-nav-back">
              ← Admin overview
            </Link>
            <Link to="/hale" className="text-[11px] uppercase tracking-[0.18em]" style={{ color: MUTED }} data-testid="hale-admin-nav-public">
              Public /hale →
            </Link>
          </nav>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-12 lg:py-16">
        <div className="mb-10">
          <HaleWordmark size="md" />
          <h1
            className="mt-5 text-3xl sm:text-4xl font-extrabold tracking-tight"
            style={{ fontFamily: '"Cormorant Garamond", serif', color: SLATE }}
            data-testid="hale-admin-title"
          >
            ToxIntel Assessor Center — Hale.
          </h1>
          <p className="mt-3 text-sm max-w-2xl" style={{ color: MUTED }}>
            The reviewer/assessor side of the Hale program. Hale applications, brand submissions,
            product reviews, the Hale Reviewed queue, documentation requests, review decisions, and
            reports — each surface preserves its existing data and review workflows. Reviewer of
            record: <span className="font-mono">regulatory@tagc.us</span>.
          </p>
        </div>

        {/* Stats strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10" data-testid="hale-admin-stats">
          {[
            { k: 'Total applications', v: stats?.total_applications },
            { k: 'In review',          v: stats?.in_review },
            { k: 'Approved brands',    v: stats?.approved_brands },
            { k: 'Active memberships', v: stats?.active_memberships },
          ].map(s => (
            <div key={s.k} className="rounded-sm border p-5" style={{ borderColor: RULE, backgroundColor: 'rgba(255,255,255,0.6)' }}>
              <p className="text-[10px] font-mono uppercase tracking-[0.25em] mb-2" style={{ color: MUTED }}>{s.k}</p>
              <p className="text-3xl font-extrabold tabular-nums" style={{ fontFamily: '"Cormorant Garamond", serif', color: SLATE }}>
                {s.v ?? '—'}
              </p>
            </div>
          ))}
        </div>

        {/* Tool grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-10">
          {HALE_TOOLS.map(t => (
            <Link
              key={t.title}
              to={t.href}
              className="rounded-sm border p-6 transition-transform hover:-translate-y-px hover:shadow-sm group"
              style={{ borderColor: RULE, backgroundColor: 'rgba(255,255,255,0.6)' }}
              data-testid={`hale-admin-tool-${t.title.toLowerCase().split(' ')[0]}`}
            >
              <t.icon size={22} weight="duotone" style={{ color: CORAL }} className="mb-3" />
              <p className="text-base font-semibold mb-1" style={{ color: SLATE }}>{t.title}</p>
              <p className="text-[12px] leading-relaxed mb-3" style={{ color: MUTED }}>{t.desc}</p>
              <span className="text-[10px] font-mono uppercase tracking-wider inline-flex items-center gap-1" style={{ color: CORAL }}>
                Open <ArrowUpRight size={11} weight="bold" />
              </span>
            </Link>
          ))}
        </div>

        {/* PILOT BRANDS — seed + inline edit */}
        <section
          className="rounded-sm border p-7 mb-10"
          style={{ borderColor: RULE, backgroundColor: 'rgba(11,20,38,0.025)' }}
          data-testid="hale-admin-pilot-section"
        >
          <div className="flex items-start justify-between gap-4 flex-wrap mb-5">
            <div>
              <p className="text-[10px] font-mono uppercase tracking-[0.3em] mb-2" style={{ color: CORAL }}>
                Phase 1 · Pilot brands
              </p>
              <h2 className="text-2xl font-extrabold tracking-tight"
                  style={{ fontFamily: '"Cormorant Garamond", serif', color: SLATE }}>
                Three pilot placeholders — tier &amp; pathway TBD.
              </h2>
              <p className="mt-2 text-sm max-w-2xl" style={{ color: MUTED }}>
                Pilot Brand A, B, and C are seeded as placeholders with tier and pathway set to <span className="font-mono">TBD</span>.
                Click <span className="font-mono">Edit</span> on any pilot to update brand name, Reviewed™/Verified™
                pathway, company size/tier, status, assigned reviewer, documents submitted, and next action.
                Re-seeding is upsert-safe — it resets the seed fields without deleting your edits to fields not in the seed.
              </p>
            </div>
            <button
              onClick={seedPilots}
              disabled={seeding}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-sm text-[12px] font-medium uppercase tracking-wider transition-transform hover:-translate-y-px shadow-sm disabled:opacity-50"
              style={{ backgroundColor: CORAL, color: SLATE }}
              data-testid="hale-admin-seed-pilots-btn"
            >
              {seeding ? 'Seeding…' : 'Re-seed pilot placeholders'} <Sparkle size={13} weight="fill" />
            </button>
          </div>

          {seedResult && (
            <div
              className="rounded-sm border p-4 text-[13px] mb-5"
              style={{
                borderColor: seedResult.ok ? RULE : 'rgba(176,49,45,0.4)',
                backgroundColor: seedResult.ok ? 'rgba(255,255,255,0.6)' : 'rgba(176,49,45,0.05)',
                color: seedResult.ok ? SLATE : '#B0312D',
              }}
              data-testid="hale-admin-seed-result"
            >
              {seedResult.ok ? (
                <>
                  <p className="font-semibold mb-1">Seed complete · total brands now {seedResult.total_brands}</p>
                  <p style={{ color: MUTED }}>
                    Created: {seedResult.created?.map(c => c.name).join(', ') || 'none'} ·
                    Updated: {(seedResult.updated || seedResult.existed)?.map(e => e.name).join(', ') || 'none'}.
                  </p>
                </>
              ) : (
                <p className="font-semibold">{seedResult.error}</p>
              )}
            </div>
          )}

          {pilots?.items?.length > 0 ? (
            <div className="overflow-x-auto" data-testid="hale-admin-pilot-table">
              <table className="w-full text-[13px]">
                <thead>
                  <tr className="text-left border-b" style={{ color: MUTED, borderColor: RULE }}>
                    <th className="py-3 pr-4 font-mono uppercase tracking-wider text-[10px]">Brand</th>
                    <th className="py-3 px-4 font-mono uppercase tracking-wider text-[10px]">Pathway</th>
                    <th className="py-3 px-4 font-mono uppercase tracking-wider text-[10px]">Tier</th>
                    <th className="py-3 px-4 font-mono uppercase tracking-wider text-[10px]">Status</th>
                    <th className="py-3 px-4 font-mono uppercase tracking-wider text-[10px]">Reviewer</th>
                    <th className="py-3 px-4 font-mono uppercase tracking-wider text-[10px]">Docs</th>
                    <th className="py-3 px-4 font-mono uppercase tracking-wider text-[10px]">Next action</th>
                    <th className="py-3 pl-4 font-mono uppercase tracking-wider text-[10px] text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {pilots.items.map(p => (
                    <tr key={p.id} className="border-b align-top" style={{ borderColor: RULE }} data-testid={`pilot-row-${p.pilot_key}`}>
                      <td className="py-4 pr-4 font-semibold" style={{ color: SLATE }}>{p.name}</td>
                      <td className="py-4 px-4">
                        <PathBadge value={p.track} options={options?.tracks} />
                      </td>
                      <td className="py-4 px-4">
                        <PathBadge value={p.tier} options={options?.tiers} />
                      </td>
                      <td className="py-4 px-4">
                        <PathBadge value={p.pilot_status} options={options?.pilot_statuses} />
                      </td>
                      <td className="py-4 px-4 text-[12px]" style={{ color: p.assigned_reviewer ? SLATE : MUTED }}>
                        {p.assigned_reviewer || '—'}
                      </td>
                      <td className="py-4 px-4 text-[12px]" style={{ color: MUTED }}>
                        {p.documents_submitted?.length || 0}
                      </td>
                      <td className="py-4 px-4 text-[12px] max-w-xs" style={{ color: MUTED }}>
                        {p.next_action || '—'}
                      </td>
                      <td className="py-4 pl-4 text-right whitespace-nowrap">
                        <button
                          onClick={() => setEditing(p)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-[11px] font-medium uppercase tracking-wider border"
                          style={{ borderColor: CORAL, color: CORAL }}
                          data-testid={`pilot-edit-${p.pilot_key}`}
                        >
                          <PencilSimple size={11} weight="bold" /> Edit
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-sm italic" style={{ color: MUTED }}>
              No pilot brands found. Click <span className="font-mono">Re-seed pilot placeholders</span> to create Pilot Brand A, B, and C.
            </p>
          )}
        </section>

        <AccessAuditPanel />

        <p className="text-[11px] font-mono uppercase tracking-[0.25em] text-center" style={{ color: MUTED }}>
          ToxIntel Assessor Center · Hale · regulatory@tagc.us
        </p>
      </main>

      {editing && (
        <PilotEditDialog
          pilot={editing}
          options={options}
          onClose={() => setEditing(null)}
          onSaved={onPilotSaved}
        />
      )}
    </div>
  );
}

function PathBadge({ value, options }) {
  if (!value) return <span className="text-[12px]" style={{ color: MUTED }}>—</span>;
  const isTbd = value === 'tbd';
  return (
    <span
      className="inline-flex items-center px-2 py-1 rounded-sm text-[10px] font-mono uppercase tracking-[0.15em]"
      style={{
        color: isTbd ? MUTED : SLATE,
        backgroundColor: isTbd ? 'rgba(11,20,38,0.05)' : 'rgba(255,78,106,0.10)',
        border: `1px solid ${isTbd ? RULE : 'rgba(255,78,106,0.25)'}`,
      }}
    >
      {labelFor(options, value)}
    </span>
  );
}


/* Access audit — one-click read-only posture snapshot. Surfaces existing
 * users, role counts, route → required-role map, recent admins, and
 * mailto-target drift. No writes. Admin-only. */
function AccessAuditPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const run = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await axios.get(`${API_URL}/api/admin/access-audit`, { withCredentials: true });
      setData(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Audit failed.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { run(); }, [run]);

  return (
    <section
      className="rounded-sm border p-7 mb-10"
      style={{ borderColor: RULE, backgroundColor: 'rgba(255,255,255,0.6)' }}
      data-testid="access-audit-section"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap mb-5">
        <div>
          <p className="text-[10px] font-mono uppercase tracking-[0.3em] mb-2" style={{ color: CORAL }}>
            Access audit · read-only
          </p>
          <h2 className="text-2xl font-extrabold tracking-tight"
              style={{ fontFamily: '"Cormorant Garamond", serif', color: SLATE }}>
            Login &amp; permission posture snapshot.
          </h2>
          <p className="mt-2 text-sm max-w-2xl" style={{ color: MUTED }}>
            Live counts, role distribution, recent admin accounts, the canonical
            route → required-role map, and drift detection on operational mailto
            targets. Refreshes on demand. Performs zero writes.
          </p>
        </div>
        <button
          onClick={run}
          disabled={loading}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-sm text-[12px] font-medium uppercase tracking-wider border disabled:opacity-50"
          style={{ borderColor: CORAL, color: CORAL }}
          data-testid="access-audit-refresh"
        >
          {loading ? 'Auditing…' : 'Refresh audit'}
        </button>
      </div>

      {error && <p className="text-[13px]" style={{ color: '#B0312D' }}>{error}</p>}

      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-testid="access-audit-summary">
            {[
              { k: 'Total users',    v: data.summary.total_users },
              { k: 'Active users',   v: data.summary.active_users },
              { k: 'Admin accounts', v: data.summary.admin_count },
              { k: 'Roles present',  v: data.summary.roles_present.join(', ') },
            ].map(s => (
              <div key={s.k} className="rounded-sm border p-4" style={{ borderColor: RULE, backgroundColor: IVORY }}>
                <p className="text-[10px] font-mono uppercase tracking-[0.25em] mb-1" style={{ color: MUTED }}>{s.k}</p>
                <p className="text-2xl font-extrabold tabular-nums" style={{ fontFamily: '"Cormorant Garamond", serif', color: SLATE }}>{s.v}</p>
              </div>
            ))}
          </div>

          <p className="text-[10px] font-mono uppercase tracking-[0.3em] mb-2" style={{ color: CORAL }}>Role breakdown</p>
          <div className="overflow-x-auto mb-6">
            <table className="w-full text-[13px]" data-testid="access-audit-roles">
              <thead><tr style={{ color: MUTED }}>
                <th className="py-2 pr-4 font-mono uppercase tracking-wider text-[10px] text-left">Role</th>
                <th className="py-2 px-4 font-mono uppercase tracking-wider text-[10px] text-left">Total</th>
                <th className="py-2 px-4 font-mono uppercase tracking-wider text-[10px] text-left">Active</th>
                <th className="py-2 pl-4 font-mono uppercase tracking-wider text-[10px] text-left">Inactive</th>
              </tr></thead>
              <tbody>
                {data.roles.map(r => (
                  <tr key={r.role} className="border-t" style={{ borderColor: RULE }}>
                    <td className="py-2 pr-4 font-semibold" style={{ color: SLATE }}>{r.role}</td>
                    <td className="py-2 px-4 tabular-nums" style={{ color: SLATE }}>{r.total}</td>
                    <td className="py-2 px-4 tabular-nums" style={{ color: SLATE }}>{r.active}</td>
                    <td className="py-2 pl-4 tabular-nums" style={{ color: MUTED }}>{r.inactive}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="text-[10px] font-mono uppercase tracking-[0.3em] mb-2" style={{ color: CORAL }}>Admin accounts</p>
          <div className="overflow-x-auto mb-6">
            <table className="w-full text-[13px]" data-testid="access-audit-admins">
              <thead><tr style={{ color: MUTED }}>
                <th className="py-2 pr-4 font-mono uppercase tracking-wider text-[10px] text-left">Email</th>
                <th className="py-2 px-4 font-mono uppercase tracking-wider text-[10px] text-left">Name</th>
                <th className="py-2 px-4 font-mono uppercase tracking-wider text-[10px] text-left">Active</th>
                <th className="py-2 px-4 font-mono uppercase tracking-wider text-[10px] text-left">Created</th>
                <th className="py-2 pl-4 font-mono uppercase tracking-wider text-[10px] text-left">Last login</th>
              </tr></thead>
              <tbody>
                {data.recent_admins.map(a => (
                  <tr key={a.email} className="border-t" style={{ borderColor: RULE }}>
                    <td className="py-2 pr-4 font-semibold" style={{ color: SLATE }}>{a.email}</td>
                    <td className="py-2 px-4" style={{ color: MUTED }}>{a.name || '—'}</td>
                    <td className="py-2 px-4" style={{ color: a.active ? SLATE : '#B0312D' }}>{a.active ? 'Yes' : 'No'}</td>
                    <td className="py-2 px-4" style={{ color: MUTED }}>{a.created_at?.slice(0,10) || '—'}</td>
                    <td className="py-2 pl-4" style={{ color: MUTED }}>{a.last_login_at?.slice(0,10) || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="text-[10px] font-mono uppercase tracking-[0.3em] mb-2" style={{ color: CORAL }}>Operational mailto targets (not login accounts)</p>
          <ul className="space-y-1 mb-6 text-[13px]" data-testid="access-audit-mailtos">
            {data.operational_mailtos.map(m => (
              <li key={m.email} className="flex items-start gap-2">
                <span style={{ color: m.drift ? '#B0312D' : CORAL }}>{m.drift ? '!' : '·'}</span>
                <span style={{ color: SLATE }} className="font-mono">{m.email}</span>
                <span style={{ color: MUTED }}>· {m.purpose}</span>
                {m.drift && <span className="text-[10px] font-mono uppercase tracking-wider" style={{ color: '#B0312D' }}>· drift: account exists</span>}
              </li>
            ))}
          </ul>

          <p className="text-[10px] font-mono uppercase tracking-[0.3em] mb-2" style={{ color: CORAL }}>Route → required role</p>
          <div className="overflow-x-auto mb-4">
            <table className="w-full text-[12px]" data-testid="access-audit-routes">
              <thead><tr style={{ color: MUTED }}>
                <th className="py-2 pr-4 font-mono uppercase tracking-wider text-[10px] text-left">Path</th>
                <th className="py-2 px-4 font-mono uppercase tracking-wider text-[10px] text-left">Kind</th>
                <th className="py-2 pl-4 font-mono uppercase tracking-wider text-[10px] text-left">Required role</th>
              </tr></thead>
              <tbody>
                {data.route_access_map.map(r => (
                  <tr key={r.path} className="border-t" style={{ borderColor: RULE }}>
                    <td className="py-1.5 pr-4 font-mono" style={{ color: SLATE }}>{r.path}</td>
                    <td className="py-1.5 px-4" style={{ color: MUTED }}>{r.kind}</td>
                    <td className="py-1.5 pl-4 font-mono" style={{ color: r.required_role === 'public' ? MUTED : CORAL }}>{r.required_role}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="text-[10px] font-mono uppercase tracking-[0.25em]" style={{ color: MUTED }} data-testid="access-audit-generated">
            Audit generated at {data.generated_at}
          </p>
        </>
      )}
    </section>
  );
}
